import { useState, useRef, useEffect } from "react";

type AudioState = {
  isAudioLoading?: boolean;
  audioError?: string | null;
  duration?: number;
  isPlaying?: boolean;
  position?: number;
};

type AudioStates = Record<string, AudioState>;
type WaveformData = Record<string, number[]>;

export const useAudioHandler = () => {
  const audioRefs = useRef<Record<string, HTMLAudioElement>>({});
  const intervalRefs = useRef<Record<string, NodeJS.Timeout>>({});
  const [audioStates, setAudioStates] = useState<AudioStates>({});
  const [waveformData, setWaveformData] = useState<WaveformData>({});
  const waveformBars = 40;

  const generateWaveformData = (): number[] => {
    return Array(waveformBars)
      .fill(0)
      .map(() => Math.random() * 50 + 10);
  };

  const loadAudio = (url: string, messageId: string) => {
    if (!url) {
      setAudioStates((prev) => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          isAudioLoading: false,
          audioError: "Invalid audio URL",
        },
      }));
      return;
    }

    setAudioStates((prev) => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        isAudioLoading: true,
        audioError: null,
      },
    }));

    try {
      if (audioRefs.current[messageId]) {
        audioRefs.current[messageId].pause();
        audioRefs.current[messageId].src = "";
        delete audioRefs.current[messageId];
      }

      const audio = new Audio(url);

      audio.onloadedmetadata = (res) => {
        setAudioStates((prev) => ({
          ...prev,
          [messageId]: {
            ...prev[messageId],
            duration: audio.duration * 1000,
            isAudioLoading: false,
          },
        }));
        setWaveformData((prev) => ({
          ...prev,
          [messageId]: generateWaveformData(),
        }));
      };

      audio.onerror = (error) => {
        setAudioStates((prev) => ({
          ...prev,
          [messageId]: {
            ...prev[messageId],
            isAudioLoading: false,
            audioError: "Failed to load audio",
          },
        }));
      };

      audio.onended = () => {
        setAudioStates((prev) => ({
          ...prev,
          [messageId]: {
            ...prev[messageId],
            isPlaying: false,
            position: 0,
          },
        }));
        clearInterval(intervalRefs.current[messageId]);
      };

      audioRefs.current[messageId] = audio;
    } catch (error: any) {
      console.error("Failed to load audio:", error.message);
      setAudioStates((prev) => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          isAudioLoading: false,
          audioError: error.message,
        },
      }));
    }
  };

  const toggleAudio = async (messageId: string) => {
    const audio = audioRefs.current[messageId];
    if (!audio || audioStates[messageId]?.isAudioLoading) {
      console.warn("No audio loaded or still loading");
      return;
    }

    if (!audio.paused) {
      audio.pause();
      clearInterval(intervalRefs.current[messageId]);
      setAudioStates((prev) => ({
        ...prev,
        [messageId]: { ...prev[messageId], isPlaying: false },
      }));
    } else {
      if (audio.ended || audio.currentTime >= audio.duration) {
        audio.currentTime = 0;
      }
      await audio.play();
      setAudioStates((prev) => ({
        ...prev,
        [messageId]: { ...prev[messageId], isPlaying: true },
      }));
      intervalRefs.current[messageId] = setInterval(() => {
        setAudioStates((prev) => ({
          ...prev,
          [messageId]: {
            ...prev[messageId],
            position: audio.currentTime * 1000,
          },
        }));
      }, 50);
    }
  };

  const cleanupAudio = () => {
    Object.values(audioRefs.current).forEach((audio) => {
      audio.pause();
      audio.src = "";
    });
    audioRefs.current = {};
    Object.values(intervalRefs.current).forEach((interval) =>
      clearInterval(interval)
    );
    intervalRefs.current = {};
  };

  useEffect(() => {
    return () => cleanupAudio();
  }, []);

  return {
    audioStates,
    setAudioStates,
    waveformData,
    loadAudio,
    toggleAudio,
    cleanupAudio,
    waveformBars,
  };
};
